import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "antd";
import "./styles.css";
import { useTranslation } from "react-i18next";
import AmapCard from "../../components/AmapCard/index.jsx";
import HotelCardList from "../../components/HotelCard/index.jsx";
import RouteCard from "../../components/RouteCard/index.jsx";
import ThinkingProcess from "../../components/ThinkingProcess/index.jsx";
import MarkdownContent from "../../components/MarkdownContent/index.jsx";
import ReferencesIcon from "../../assets/references-icon.png";
import { normalizeMarkdownForRender } from "../../utils/markdownNormalize";
import { getEsHotelSearchHotels } from "../../utils/esHotelSearch";

const API_BASE_URL = "https://chinapedia-test.connectnowai.com";
const MARKDOWN_SSE_EVENT_NAMES = new Set([
  "itinerary_planning_sse",
  "tripai_sse",
]);

const CustomerIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xlink="http://www.w3.org/1999/xlink"
      fill="none"
      version="1.1"
      width="50"
      height="50.000003814697266"
      viewBox="0 0 50 50.000003814697266"
    >
      <defs>
        <clipPath id="master_svg0_31_35016">
          <rect x="0" y="0" width="50" height="50.000003814697266" rx="0" />
        </clipPath>
      </defs>
      <g clip-path="url(#master_svg0_31_35016)">
        <g>
          <ellipse
            cx="24.375"
            cy="24.375"
            rx="19.375"
            ry="19.375"
            fill="#FFFFFF"
            fill-opacity="1"
          />
        </g>
        <g>
          <path
            d="M25,0C38.8,0,50,11.2,50,25C50,38.8,38.8,50,25,50C11.2,50,0,38.8,0,25C0,11.2,11.2,0,25,0ZM10.0583,33.5407C13.7271,39.0173,19.2378,42.5,25.3993,42.5C31.5607,42.5,37.0715,39.0173,40.7402,33.5407C36.7212,29.793,31.328,27.5,25.3993,27.5C19.4705,27.5,14.0774,29.793,10.0583,33.5407ZM25,22.5C29.1423,22.5,32.5,19.1421,32.5,15C32.5,10.8579,29.1423,7.5,25,7.5C20.8577,7.5,17.5,10.8579,17.5,15C17.5,19.1421,20.8577,22.5,25,22.5Z"
            fill="#919499"
            fill-opacity="1"
          />
        </g>
      </g>
    </svg>
  );
};
const AddIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xlink="http://www.w3.org/1999/xlink"
      fill="none"
      version="1.1"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <defs>
        <clipPath id="master_svg0_31_23287">
          <rect x="0" y="0" width="24" height="24" rx="0" />
        </clipPath>
      </defs>
      <g clip-path="url(#master_svg0_31_23287)">
        <g>
          <path
            d="M12,22C6.47715,22,2,17.5228,2,12C2,6.47715,6.47715,2,12,2C17.5228,2,22,6.47715,22,12C22,17.5228,17.5228,22,12,22ZM11,11L7,11L7,13L11,13L11,17L13,17L13,13L17,13L17,11L13,11L13,7L11,7L11,11Z"
            fill="#FFFFFF"
            fill-opacity="1"
          />
        </g>
      </g>
    </svg>
  );
};

const SharePage = () => {
  const { t } = useTranslation();
  // 是否是移动端
  const [isMobile, setIsMobile] = useState(true);
  const [messageId, setMessageId] = useState(null); //当前分享的messageId
  const [shareAgentData, setShareAgentData] = useState([]); //当前分享的数据
  const [searchParams] = useSearchParams();

  const normalizeItineraryMarkdownChunk = (prevContent = "", chunk = "") => {
    if (!chunk) return "";
    let normalized = String(chunk).replace(/\r\n/g, "\n");
    normalized = normalized.replace(/^---+\s*(#{1,6}\s)/, "$1");

    const enforceStrongBoundarySpacing = (text = "") => {
      if (!text.includes("**")) return text;
      const isWordLike = (ch = "") => /[A-Za-z0-9\u4E00-\u9FFF]/.test(ch);

      let output = "";
      let inStrong = false;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === "*" && text[i + 1] === "*") {
          const prevChar = output[output.length - 1] || "";
          const nextChar = text[i + 2] || "";

          if (!inStrong && isWordLike(prevChar) && prevChar !== " ") {
            output += " ";
          }

          output += "**";
          inStrong = !inStrong;

          if (!inStrong && isWordLike(nextChar) && nextChar !== " ") {
            output += " ";
          }

          i += 1;
          continue;
        }
        output += text[i];
      }
      return output;
    };
    normalized = enforceStrongBoundarySpacing(normalized);

    const hasPrev = prevContent.length > 0;
    const prevEndsWithNewline = /\n\s*$/.test(prevContent);
    const isStandaloneHr = /^\s*(?:---+|\*\*\*+|___+)\s*$/.test(normalized);
    if (isStandaloneHr) {
      const prevHasBlankLine = /\n\s*\n\s*$/.test(prevContent);
      const prefix = !hasPrev
        ? ""
        : prevHasBlankLine
          ? ""
          : prevEndsWithNewline
            ? "\n"
            : "\n\n";
      return `${prefix}${normalized.trim()}\n`;
    }

    if (!hasPrev || prevEndsWithNewline) return normalized;

    if (/^\s*(?:#{1,6}\s|```|~~~)/.test(normalized)) {
      return `\n\n${normalized}`;
    }
    if (/^\s*(?:\d+[.)]\s|[-+*]\s|>\s?|\|)/.test(normalized)) {
      return `\n${normalized}`;
    }
    if (/^\s*\[\^[^\]]+\]:/.test(normalized)) {
      return `\n${normalized}`;
    }

    const looksStandaloneStrongLine =
      /^\s*(?:\*\*[^*\n]+(?:\[[^\]]+\]\([^)]+\))?[^*\n]*\*\*|__[^_\n]+__)\s*$/.test(
        normalized,
      );
    const looksStandaloneLinkLine =
      /^\s*(?:\*\*)?\[[^\]]+\]\([^)]+\)(?:\*\*)?\s*$/.test(normalized);
    if (looksStandaloneStrongLine || looksStandaloneLinkLine) {
      return `\n${normalized}`;
    }

    return normalized;
  };

  const extractCityHotelSseData = (buffer = "") => {
    const source = String(buffer || "");
    const hotelCards = [];
    if (!source) {
      return { hotelCards, content: "" };
    }

    let depth = 0;
    let objStart = -1;
    let lastTextStart = 0;
    const textParts = [];

    for (let i = 0; i < source.length; i++) {
      const ch = source[i];
      if (ch === "{") {
        if (depth === 0) objStart = i;
        depth += 1;
      } else if (ch === "}" && depth > 0) {
        depth -= 1;
        if (depth === 0 && objStart >= 0) {
          const segment = source.slice(objStart, i + 1);
          let parsed = null;
          try {
            parsed = JSON.parse(segment);
          } catch (_) {}

          if (parsed?.type === "hotel" && parsed?.hotel_data) {
            hotelCards.push(parsed);
            textParts.push(source.slice(lastTextStart, objStart));
            lastTextStart = i + 1;
          }

          objStart = -1;
        }
      }
    }

    textParts.push(source.slice(lastTextStart));
    const content = normalizeMarkdownForRender(textParts.join(""), {
      source: "city_hotel_sse",
    });

    return { hotelCards, content };
  };

  const formatDocDetails = (docDetails) => {
    if (!Array.isArray(docDetails)) return [];
    return docDetails
      .map((item) => {
        const msg = item?.doc_message || {};
        const link = msg?.url || "";
        const title = msg?.title || "";
        if (!link || !title) return null;
        return {
          link,
          title,
          img: msg?.meta_images || "",
          time: msg?.publish_time || "",
        };
      })
      .filter(Boolean);
  };

  const extractCityHotelToolResultContent = (toolResults) => {
    const answerText = toolResults?.city_hotel?.answer_text || "";
    if (!answerText) return "";
    return normalizeMarkdownForRender(answerText, {
      source: "city_hotel_sse",
    });
  };

  const pickBestCityHotelContent = (candidates = []) => {
    const normalizedCandidates = candidates
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    if (normalizedCandidates.length === 0) return "";
    if (normalizedCandidates.length === 1) return normalizedCandidates[0];
    const maxLength = Math.max(
      ...normalizedCandidates.map((text) => text.length),
    );
    const structuredContent = normalizedCandidates[0];
    const structuredLineCount = structuredContent.split("\n").length;
    const structuredBlockScore =
      (structuredContent.match(/\n#{1,6}\s/g) || []).length +
      (structuredContent.match(/\n[-+*]\s/g) || []).length +
      (structuredContent.match(/\[[^\]]+\]\([^)]+\)/g) || []).length;

    if (
      structuredContent.length >= maxLength * 0.45 &&
      structuredLineCount >= 5 &&
      structuredBlockScore >= 2
    ) {
      return structuredContent;
    }

    const score = (text) => {
      const headingScore = (text.match(/\n#{1,6}\s/g) || []).length * 50;
      const listScore = (text.match(/\n[-+*]\s/g) || []).length * 18;
      const linkScore = (text.match(/\[[^\]]+\]\([^)]+\)/g) || []).length * 8;
      const lineCount = text.split("\n").length;
      const newlineScore = Math.max(0, lineCount - 1) * 5;
      const oddStrongPenalty =
        (text.match(/\*\*/g) || []).length % 2 === 0 ? 0 : 280;
      const longSingleBlockPenalty =
        lineCount <= 3 && text.length > 500 ? 380 : 0;
      return (
        text.length +
        headingScore +
        listScore +
        linkScore +
        newlineScore -
        oddStrongPenalty -
        longSingleBlockPenalty
      );
    };

    return normalizedCandidates.reduce((best, current) =>
      score(current) > score(best) ? current : best,
    );
  };

  const parseStructuredEvents = (events) => {
    const mapCards = [];
    const routeCards = [];
    const hotelCards = [];
    const thinkingSteps = [];
    let itineraryContent = "";
    let hotelBuffer = "";

    (Array.isArray(events) ? events : []).forEach((e) => {
      const name = e?.name;
      const payload = e?.payload || {};
      const pType = payload.type;
      const esHotelCards = getEsHotelSearchHotels(name, payload);

      if (pType === "thinking") {
        const text = (payload.text || "").trim();
        if (text && thinkingSteps[thinkingSteps.length - 1] !== text) {
          thinkingSteps.push(text);
        }
      }

      if (esHotelCards.length > 0) {
        hotelCards.push(...esHotelCards);
        return;
      }

      if (name === "maps_route_planning") {
        if (pType === "data" && payload.route) {
          routeCards.push(payload);
        }
        return;
      }

      if (MARKDOWN_SSE_EVENT_NAMES.has(name)) {
        if ((pType === "answer" || pType === "raw") && payload.text) {
          itineraryContent += normalizeItineraryMarkdownChunk(
            itineraryContent,
            payload.text,
          );
        }
        return;
      }

      if (name === "city_hotel_sse") {
        if (pType === "answer" || pType === "raw") {
          const text = payload.text || "";
          hotelBuffer += text || (pType === "raw" ? "\n" : "");
        }
        return;
      }

      if (payload?.pois && payload.pois.length > 0) {
        mapCards.push({
          tool: payload.tool || name,
          keywords: payload.keywords || "",
          location: payload.location || "",
          radius: payload.radius,
          pois: payload.pois,
          pois_count: payload.pois_count,
        });
      }
    });

    const { hotelCards: legacyHotelCards, content: cityHotelContent } =
      extractCityHotelSseData(hotelBuffer);
    hotelCards.push(...legacyHotelCards);

    return {
      mapCards,
      routeCards,
      hotelCards,
      thinkingSteps,
      itineraryContent,
      cityHotelContent,
    };
  };

  const formatServerHistory = (dataArr) => {
    const result = [];
    (Array.isArray(dataArr) ? dataArr : []).forEach((item) => {
      const msgId = item.msg_id;
      const {
        mapCards,
        routeCards,
        hotelCards,
        thinkingSteps,
        itineraryContent,
        cityHotelContent,
      } = parseStructuredEvents(item.structured_events);
      const cityHotelToolResultContent = extractCityHotelToolResultContent(
        item.tool_results,
      );
      const hasCityHotelSignal =
        Boolean(item?.tool_results?.city_hotel) ||
        (Array.isArray(item?.structured_events)
          ? item.structured_events.some(
              (eventItem) => eventItem?.name === "city_hotel_sse",
            )
          : false);
      const normalizedRobotAnswer = hasCityHotelSignal
        ? normalizeMarkdownForRender(item.robot_answer || "", {
            source: "city_hotel_sse",
          })
        : item.robot_answer || "";
      const docDetails = formatDocDetails(item.doc_detail);
      const ragFallbackDocs = (
        Array.isArray(item.rag_documents) ? item.rag_documents : []
      )
        .map((doc) => {
          const meta = doc?.metadata || {};
          const link = meta?.url || "";
          const title = meta?.title || "";
          if (!link || !title) return null;
          return {
            link,
            title,
            img: meta?.meta_images || "",
            time: meta?.publish_time || "",
          };
        })
        .filter(Boolean);
      const historyDocuments =
        docDetails.length > 0 ? docDetails : ragFallbackDocs;
      const aiContent =
        itineraryContent && itineraryContent.trim()
          ? itineraryContent
          : hasCityHotelSignal
            ? pickBestCityHotelContent([
                cityHotelContent,
                cityHotelToolResultContent,
                normalizedRobotAnswer,
              ])
            : normalizedRobotAnswer;
      const supportStatus = Number(item.support_status);

      result.push({
        msgId: `${msgId}_user`,
        content: item.questions || "",
        role: "user",
        isEditing: false,
        isPlaying: false,
        messageId: msgId,
        language: "",
        history: true,
      });

      result.push({
        msgId: `${msgId}_ai`,
        content: aiContent,
        role: "system",
        isEditing: false,
        isPlaying: false,
        messageId: msgId,
        documents: historyDocuments,
        audioUrl: /^https?:\/\//.test(item.audio_s3_url || "")
          ? item.audio_s3_url
          : "",
        audioS3Url: item.audio_s3_url || "",
        audioUrls: /^https?:\/\//.test(item.audio_s3_url || "")
          ? [item.audio_s3_url]
          : [],
        mapCards,
        routeCards,
        hotelCards,
        isFinished: true,
        like: supportStatus === 1,
        downVote: supportStatus === 2,
        language: "",
        thinkingSteps: thinkingSteps.length ? thinkingSteps : undefined,
        history: true,
      });
    });
    return result;
  };

  const queryShareDetail = useCallback(() => {
    fetch(
      `${API_BASE_URL}/history/message?msg_id=${encodeURIComponent(messageId)}`,
    )
      .then((res) => res.json())
      .then((json) => {
        const data = json?.data ?? json;
        const dataArr = Array.isArray(data) ? data : data ? [data] : [];
        setShareAgentData(formatServerHistory(dataArr));
      })
      .catch((err) => console.error("请求失败:", err));
  }, [messageId]);

  useEffect(() => {
    const nextIsMobile =
      /Android|webOS|iPhone|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
    setIsMobile(nextIsMobile);

    const nextMessageId = searchParams.get("messageId");
    if (nextMessageId) {
      setMessageId(nextMessageId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (messageId) {
      queryShareDetail();
    }
  }, [messageId, queryShareDetail]);

  const renderSourceArticles = (itemData) => {
    if (!itemData?.isFinished || !itemData.documents?.length) return null;

    return (
      <div className="referencesContainer">
        <p className="referencesTitle">
          <img src={ReferencesIcon} alt="" />
          {t("articles.source")}
        </p>
        <div className="referencesItemBody">
          <div className="referencesItemContainer">
            {itemData.documents.map((documentItem, index) => {
              const hasDocumentImage = Boolean(documentItem.img);
              const referencesItemTopClassName = hasDocumentImage
                ? "referencesItemTop"
                : "referencesItemTop noImage";
              return (
                <a
                  key={`${documentItem.link}-${index}`}
                  href={documentItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="referencesItem">
                    <div className={referencesItemTopClassName}>
                      {hasDocumentImage && (
                        <img
                          src={documentItem.img}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.parentElement?.classList.add(
                              "noImage",
                            );
                          }}
                        />
                      )}
                      <p>{documentItem.title}</p>
                    </div>
                    <div className="referencesItemBottom">
                      {documentItem.time}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMessage = (itemData, index) => {
    if (itemData.role === "user") {
      return (
        <div
          key={itemData.msgId || `user-${index}`}
          className="customerContainer message-animate message-animate-right"
        >
          <div className="customerMessage">
            <div
              className="customerText"
              dir={itemData.language === "ar" ? "rtl" : "ltr"}
            >
              {itemData?.content}
            </div>
            <CustomerIcon />
          </div>
        </div>
      );
    }

    const thinkingSteps = itemData.thinkingSteps || [];
    const showThinkingBlock = thinkingSteps.length > 0;
    const showAgentHeader = itemData.content !== "" || showThinkingBlock;

    return (
      <div
        key={itemData.msgId || `system-${index}`}
        className="agentMessage message-animate message-animate-left"
      >
        {showAgentHeader ? <div className="agentBg">Pedia</div> : null}
        {showThinkingBlock && (
          <>
            <ThinkingProcess steps={thinkingSteps} isThinking={false} />
            <div className="flex-break" />
          </>
        )}
        {itemData.content && (
          <MarkdownContent
            content={itemData.content}
            className={`agentMessage_content${
              showThinkingBlock ? " has-thinking" : ""
            }`}
            dir={itemData.language === "ar" ? "rtl" : "ltr"}
          />
        )}
        {itemData.mapCards?.map((card, cardIdx) => (
          <AmapCard
            key={`map-${itemData.msgId}-${cardIdx}`}
            pois={card.pois}
            location={card.location}
            keywords={card.keywords}
          />
        ))}
        {itemData.routeCards?.map((route, routeIdx) => (
          <RouteCard
            key={`route-${itemData.msgId}-${routeIdx}`}
            routeData={route}
          />
        ))}
        {itemData.hotelCards?.length > 0 && (
          <HotelCardList
            key={`hotels-${itemData.msgId}`}
            hotels={itemData.hotelCards}
          />
        )}
        {renderSourceArticles(itemData)}
      </div>
    );
  };

  return (
    <>
      {isMobile ? (
        <div className="phoneShareContainer">
          <div className="shareTitle">{t("app.title")}</div>
          <div className="shareDetailContent">
            {shareAgentData?.map(renderMessage)}
          </div>
          <div className="shareButton">
            <Button type="primary" icon={<AddIcon />}>
              Start Chat
            </Button>
          </div>
        </div>
      ) : (
        <div className="pcShareContainer">
          <div className="shareTitle">{t("app.title")}</div>
          <div className="shareDetailContent">
            {shareAgentData?.map(renderMessage)}
          </div>
          <div className="shareButton">
            <a
              href="https://chinapedia-test.goclouds.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button type="primary" icon={<AddIcon />}>
                Start Chat
              </Button>
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default SharePage;
